import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Typography, 
  Space, 
  Tag,
  Tooltip,
  Empty,
  Spin
} from 'antd';
import { 
  DownloadOutlined,
  PaperClipOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { AttachmentManagerProps, SubtaskAttachment } from '../../../types/plan-sub-task.ts';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

/**
 * Attachment Manager Component
 * Displays attachment list in table format with download functionality
 */
const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  subtaskCode,
  attachments,
  onUpload,
  onDelete,
  loading,
  showCard = true
}) => {
  const { t } = useTranslation();
  const [localAttachments, setLocalAttachments] = useState<SubtaskAttachment[]>(attachments);

  // Update local attachments when props change
  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file type tag color
  const getFileTypeColor = (fileType: string): string => {
    if (fileType.includes('image/')) return 'green';
    if (fileType.includes('pdf')) return 'red';
    if (fileType.includes('word') || fileType.includes('document')) return 'blue';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'orange';
    if (fileType.includes('text')) return 'default';
    return 'purple';
  };

  // Format upload time
  const formatUploadTime = (timeString: string) => {
    return new Date(timeString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle file download
  const handleDownload = (attachment: SubtaskAttachment) => {
    // In a real application, this would trigger a file download
    if (attachment.downloadUrl) {
      window.open(attachment.downloadUrl, '_blank');
    } else {
      // Simulate download
      console.log(`${t('subtask.attachments.downloadFile')}: ${attachment.fileName}`);
    }
  };

  // Get file extension from filename
  const getFileExtension = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toUpperCase();
    return extension || 'FILE';
  };

  // Table columns definition
  const columns: ColumnsType<SubtaskAttachment> = [
    {
      title: t('subtask.attachments.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: '30%',
      render: (fileName: string, record: SubtaskAttachment) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleDownload(record)}
            style={{ padding: 0, height: 'auto', fontWeight: 500 }}
          >
            {fileName}
          </Button>
        </Space>
      ),
    },
    {
      title: t('subtask.attachments.fileFormat'),
      dataIndex: 'fileType',
      key: 'fileType',
      width: '15%',
      render: (fileType: string, record: SubtaskAttachment) => (
         <Tag color={getFileTypeColor(fileType)}>
           {getFileExtension(record.fileName)}
         </Tag>
       ),
    },
    {
      title: t('subtask.attachments.fileSize'),
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: '15%',
      render: (fileSize: number) => (
        <Text type="secondary">{formatFileSize(fileSize)}</Text>
      ),
    },
    {
      title: t('subtask.attachments.uploader'),
      dataIndex: 'uploader',
      key: 'uploader',
      width: '15%',
      render: (uploader: string) => (
        <Text>{uploader}</Text>
      ),
    },
    {
      title: t('subtask.attachments.uploadTime'),
      dataIndex: 'uploadTime',
      key: 'uploadTime',
      width: '20%',
      render: (uploadTime: string) => (
        <Text type="secondary">{formatUploadTime(uploadTime)}</Text>
      ),
    },
    {
      title: t('subtask.attachments.actions'),
      key: 'action',
      width: '5%',
      render: (_, record: SubtaskAttachment) => (
        <Tooltip title={t('subtask.attachments.download')}>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            size="small"
          />
        </Tooltip>
      ),
    },
  ];

  // Render content
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">{t('subtask.attachments.loading')}</Text>
          </div>
        </div>
      );
    }

    if (localAttachments.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('subtask.attachments.noAttachments')}
          style={{ padding: '40px 0' }}
        />
      );
    }

    return (
      <Table
        columns={columns}
        dataSource={localAttachments}
        rowKey="id"
        pagination={false}
        size="small"
        style={{ marginTop: showCard ? 16 : 0 }}
        scroll={{ y: 500 }}
      />
    );
  };

  const title = (
    <Space>
      <PaperClipOutlined />
      <span>{t('subtask.attachments.title')}</span>
      {localAttachments.length > 0 && (
        <Text type="secondary">({localAttachments.length})</Text>
      )}
    </Space>
  );

  if (showCard) {
    return (
      <Card 
        title={title}
        className="detail-section"
      >
        {renderContent()}
      </Card>
    );
  }

  return (
    <div>
      {renderContent()}
    </div>
  );
};

export default AttachmentManager;